import {
  isoKw,
  montagVon,
  tagesbeginn,
  tagesschluessel,
  tagKompakt,
  tagNachPersonen,
  TAGESLAGEN,
  TAGESLAGE_LABELS,
  type Tageslage,
  type TaskSource,
} from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { erledigtProTagQuery, wochenAufgabenQuery, type LeitstandHeute } from '../../lib/queries'
import { AbwesenheitsHinweis, angabeAusLage, useAbwesenheiten } from '../Abwesenheit'
import { Aktivitaetsbericht } from '../Aktivitaetsbericht'
import { AufgabenVerweis, useAufgabeOeffnen } from '../AufgabeKontext'
import { AufgabenPopup, type AufgabenGruppe } from '../AufgabenPopup'
import { WochenStreifen } from '../diagramm/WochenStreifen'
import { TagesPopup, type TagesAuswahl } from '../TagesPopup'
import { Card, CardPad, SourceBadge } from '../ui/primitives'
import { Zuweisen } from '../Zuweisen'
import { NiemandZugewiesen } from './Niemand'
import { punktText, type Punkt } from './punkt'

type Kachel = Punkt['kacheln'][number]
type TeamZeile = LeitstandHeute['team']['zeilen'][number]
type Popup = { titel: string; untertitel: string; gruppen: AufgabenGruppe[] }

/** So viele Punkte stehen offen – der Rest hinter „+ n weitere". Eine lange Liste liest keiner. */
const SICHTBAR = 10

/** Als `string`, nicht als Literal: die Routen sind nicht typregistriert (wie in `Trichter.tsx`). */
const ZUM_EINGANG: string = '/eingang'

const anzahl = (n: number) => `${n} ${n === 1 ? 'Aufgabe' : 'Aufgaben'}`

/**
 * **Reiter „Heute"** – das Daily ([E-358](../../../../../docs/ENTSCHEIDUNGEN.md#e-358)).
 *
 * Von oben nach unten die Reihenfolge, in der die Runde fragt: *wo brennt es* (Lage und
 * Eingreifen), *wer hat wie viel* (Team), *was läuft diese Woche* (Die Woche). Kein Zeitraum:
 * „Heute" ist ein Zeitpunkt, und ein Umschalter, der hier nichts verändert, war im alten
 * Leitstand die Quelle der Verwirrung.
 */
export function LeitstandHeute({ daten }: { daten: LeitstandHeute | undefined }) {
  const [filter, setFilter] = useState<Kachel | null>(null)
  const [popup, setPopup] = useState<Popup | null>(null)
  const [tag, setTag] = useState<TagesAuswahl | null>(null)
  const [zuweisenFuer, setZuweisenFuer] = useState<{ id: string; title: string } | null>(null)
  const [berichtFuer, setBerichtFuer] = useState<string | null>(null)

  if (!daten) return <div className="py-2 text-[13px] text-ink2">Lädt …</div>

  const { kacheln } = daten.lage
  const zeigen = (titel: string, ids: string[], gruppen?: AufgabenGruppe[]) =>
    setPopup({ titel, untertitel: anzahl(ids.length), gruppen: gruppen ?? [{ ids }] })

  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 xl:grid-cols-6">
        <LageKachel
          ton="kritisch"
          label="Sofort, nicht gezogen"
          wert={kacheln.sofort.ids.length}
          zusatz={
            kacheln.sofort.aeltesteStunden === null
              ? 'nichts liegt'
              : kacheln.sofort.aeltesteStunden < 48
                ? `älteste seit ${kacheln.sofort.aeltesteStunden} Std.`
                : `älteste seit ${Math.floor(kacheln.sofort.aeltesteStunden / 24)} Tagen`
          }
          aktiv={filter === 'sofort'}
          onClick={() => setFilter(filter === 'sofort' ? null : 'sofort')}
        />
        <LageKachel
          ton="kritisch"
          label="Überfällig"
          wert={kacheln.ueberfaellig.ids.length}
          zusatz={`${kacheln.ueberfaellig.beiAbwesenden} bei Abwesenden · ${kacheln.ueberfaellig.ohnePerson} ohne Person`}
          aktiv={filter === 'ueberfaellig'}
          onClick={() => setFilter(filter === 'ueberfaellig' ? null : 'ueberfaellig')}
        />
        <LageKachel
          ton="warnung"
          label="Fällig ≤ 5 T, niemand dran"
          wert={kacheln.niemand.ids.length}
          zusatz={`+ ${kacheln.niemand.ohneDatum} ohne Datum`}
          aktiv={filter === 'niemand'}
          onClick={() => setFilter(filter === 'niemand' ? null : 'niemand')}
        />
        <LageKachel
          ton="warnung"
          label="Eingang ungesichtet"
          wert={kacheln.eingang.ids.length}
          zusatz={`${kacheln.eingang.ueber7} davon > 7 Tage`}
          aktiv={filter === 'eingang'}
          onClick={() => setFilter(filter === 'eingang' ? null : 'eingang')}
        />
        <LageKachel
          ton="warnung"
          label="Abnahme wartet"
          wert={kacheln.abnahme.ids.length}
          zusatz={`${kacheln.abnahme.ueber7} seit > 7 Tagen`}
          aktiv={filter === 'abnahme'}
          onClick={() => setFilter(filter === 'abnahme' ? null : 'abnahme')}
        />
        <LageKachel
          ton="gut"
          label={`Erledigt ${wochentagKurz(daten.team.letzterWerktag)}`}
          wert={daten.team.erledigt.anzahl}
          zusatz={`Ø ${daten.team.erledigt.schnitt.toLocaleString('de-DE')} je Werktag (30 T)`}
          aktiv={false}
          onClick={() => setTag({ personId: null, name: 'Team', tag: daten.team.letzterWerktag })}
        />
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]">
        <Eingreifen
          punkte={daten.lage.punkte}
          filter={filter}
          filterLoesen={() => setFilter(null)}
          zuweisen={(id, title) => setZuweisenFuer({ id, title })}
          zeigen={zeigen}
        />
        <TeamTabelle
          team={daten.team}
          zeigen={zeigen}
          tagZeigen={setTag}
          berichtZeigen={setBerichtFuer}
        />
      </div>

      <NiemandZugewiesen
        niemand={daten.team.zeilen.find((z) => z.personId === null)}
        zuweisen={(id, title) => setZuweisenFuer({ id, title })}
      />

      <DieWoche
        reihenfolge={daten.team.zeilen.map((z) => z.name)}
        zeigen={(p) => setPopup(p)}
        tagZeigen={setTag}
      />

      {popup && (
        <AufgabenPopup
          titel={popup.titel}
          untertitel={popup.untertitel}
          gruppen={popup.gruppen}
          schliessen={() => setPopup(null)}
        />
      )}
      {tag && <TagesPopup auswahl={tag} schliessen={() => setTag(null)} />}
      {zuweisenFuer && (
        <Zuweisen
          aufgabeId={zuweisenFuer.id}
          titel={zuweisenFuer.title}
          schliessen={() => setZuweisenFuer(null)}
        />
      )}
      {berichtFuer && (
        <Aktivitaetsbericht personId={berichtFuer} schliessen={() => setBerichtFuer(null)} />
      )}
    </div>
  )
}

/** „Mi" für einen Tagesschlüssel – die Spalte „zuletzt" heißt nach dem Tag, den sie meint. */
function wochentagKurz(tag: string): string {
  return new Intl.DateTimeFormat('de-DE', { weekday: 'short' })
    .format(tagesbeginn(tag))
    .replace('.', '')
}

/* ── Lage ─────────────────────────────────────────────────────────────────── */

const TON = {
  kritisch: { streifen: 'bg-pool-sofort', zahl: 'text-[#b02a2a]' },
  warnung: { streifen: 'bg-[#c98a12]', zahl: 'text-[#8a6100]' },
  gut: { streifen: 'bg-[#0ca30c]', zahl: 'text-ink' },
} as const

/**
 * Eine Kachel der Lage-Leiste. **Eine Null ist grau**: sie ist die gute Nachricht, und rot
 * hieße „handeln" (dieselbe Regel wie in der Liegezeiten-Matrix).
 */
function LageKachel({
  ton,
  label,
  wert,
  zusatz,
  aktiv,
  onClick,
}: {
  ton: keyof typeof TON
  label: string
  wert: number
  zusatz: string
  aktiv: boolean
  onClick: () => void
}) {
  const t = TON[ton]
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={aktiv}
      className={`relative grid cursor-pointer gap-px overflow-hidden rounded-card border bg-surface py-2.5 pr-3 pl-3.5 text-left hover:border-line2 ${
        aktiv ? 'border-ink shadow-[inset_0_0_0_1px_var(--color-ink)]' : 'border-line'
      }`}
    >
      <span
        aria-hidden
        className={`absolute inset-y-0 left-0 w-[3px] ${wert === 0 && ton !== 'gut' ? 'bg-line2' : t.streifen}`}
      />
      <span className="text-[11.5px] font-medium text-ink2">{label}</span>
      <span
        className={`text-[22px] leading-tight font-bold tracking-[-0.4px] ${wert === 0 ? 'text-muted' : t.zahl}`}
      >
        {wert}
      </span>
      <span className="text-[11px] text-muted">{zusatz}</span>
    </button>
  )
}

/* ── Eingreifen ───────────────────────────────────────────────────────────── */

const SCHWERE_STREIFEN: Record<Punkt['schwere'], string> = {
  kritisch: 'bg-pool-sofort',
  warnung: 'bg-[#c98a12]',
  hinweis: 'bg-accent',
}
const SCHWERE_TEXT: Record<Punkt['schwere'], string> = {
  kritisch: 'text-[#b02a2a]',
  warnung: 'text-[#8a6100]',
  hinweis: 'text-ink2',
}

const FILTER_NAME: Record<Kachel, string> = {
  sofort: 'Sofort',
  ueberfaellig: 'Überfällig',
  niemand: 'niemand dran',
  eingang: 'Eingang',
  abnahme: 'Abnahme',
}

function Eingreifen({
  punkte,
  filter,
  filterLoesen,
  zuweisen,
  zeigen,
}: {
  punkte: Punkt[]
  filter: Kachel | null
  filterLoesen: () => void
  zuweisen: (id: string, title: string) => void
  zeigen: (titel: string, ids: string[]) => void
}) {
  const [alle, setAlle] = useState(false)
  const aufgabeOeffnen = useAufgabeOeffnen()
  const navigate = useNavigate()

  const gefiltert = filter === null ? punkte : punkte.filter((p) => p.kacheln.includes(filter))
  const gezeigt = alle ? gefiltert : gefiltert.slice(0, SICHTBAR)
  const rest = gefiltert.length - gezeigt.length

  const handeln = (p: Punkt) => {
    const t = punktText(p)
    if (t.aktion === 'zuweisen' && p.aufgabe) return zuweisen(p.aufgabe.id, p.aufgabe.titel)
    if (t.aktion === 'oeffnen' && p.aufgabe) return aufgabeOeffnen(p.aufgabe.id)
    if (t.aktion === 'eingang') return void navigate({ to: ZUM_EINGANG })
    zeigen(`${t.was} · ${t.warum}`, p.ids)
  }

  return (
    <Card>
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2">
          <b className="text-[13.5px]">Eingreifen</b>
          <span className="text-xs text-muted">
            {filter === null
              ? `${punkte.length} ${punkte.length === 1 ? 'Punkt' : 'Punkte'} · nach Dringlichkeit`
              : `${gefiltert.length} von ${punkte.length} · ${FILTER_NAME[filter]}`}
          </span>
          {filter !== null && (
            <button
              type="button"
              onClick={filterLoesen}
              className="ml-auto cursor-pointer text-xs font-semibold text-accent-deep hover:underline"
            >
              Filter lösen
            </button>
          )}
        </div>

        {gefiltert.length === 0 ? (
          <p className="mt-3 text-[13px] text-ink2">
            {filter === null
              ? 'Nichts, das heute eingreifen verlangt.'
              : 'Dazu steht gerade nichts an.'}
          </p>
        ) : (
          <ul className="mt-2">
            {gezeigt.map((p) => {
              const t = punktText(p)
              const einzelTitel = p.aufgabe && t.was === p.aufgabe.titel
              return (
                <li
                  key={p.schluessel}
                  className="grid grid-cols-[4px_minmax(0,1fr)_auto] items-center gap-2.5 border-t border-line py-2 first:border-t-0"
                >
                  <span
                    aria-hidden
                    className={`self-stretch rounded-sm ${SCHWERE_STREIFEN[p.schwere]}`}
                  />
                  <div className="min-w-0">
                    <div className={`text-[11px] font-semibold ${SCHWERE_TEXT[p.schwere]}`}>
                      {t.warum}
                    </div>
                    <div className="truncate text-[13px] leading-snug font-semibold">
                      {einzelTitel ? (
                        <AufgabenVerweis
                          aufgabeId={p.aufgabe!.id}
                          className="max-w-full cursor-pointer truncate text-left hover:underline"
                          title={t.was}
                        >
                          {t.was}
                        </AufgabenVerweis>
                      ) : (
                        t.was
                      )}
                    </div>
                    {(t.zusatz || p.aufgabe || p.abwesendBis) && (
                      <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-[11.5px] text-muted">
                        {p.aufgabe && einzelTitel && (
                          <SourceBadge source={p.aufgabe.quelle as TaskSource} />
                        )}
                        {p.abwesendBis && (
                          <AbwesenheitsHinweis
                            angabe={{
                              abwesendBis: p.abwesendBis,
                              halberTag: false,
                              naechsteVon: null,
                              naechsteBis: null,
                            }}
                          />
                        )}
                        {t.zusatz && <span className="truncate">{t.zusatz}</span>}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handeln(p)}
                    aria-label={`${t.was}: ${t.knopf}`}
                    className={`cursor-pointer rounded-lg border px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${
                      t.aktion === 'zuweisen'
                        ? 'border-accent bg-accent text-white hover:bg-accent-deep'
                        : 'border-line2 bg-surface hover:bg-[#f3f2ef]'
                    }`}
                  >
                    {t.knopf}
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        {(rest > 0 || (alle && gefiltert.length > SICHTBAR)) && (
          <button
            type="button"
            onClick={() => setAlle(!alle)}
            className="mt-2 cursor-pointer text-xs font-semibold text-accent-deep hover:underline"
          >
            {alle ? 'weniger zeigen' : `+ ${rest} weitere`}
          </button>
        )}
      </CardPad>
    </Card>
  )
}

/* ── Team ─────────────────────────────────────────────────────────────────── */

/**
 * **Eine Tabelle statt vier** – Kapazität, Personen-Tabelle, Geplant und „Wie viel es ist"
 * (E-358). Eine Zeile je Person, **eine** Reihenfolge (alphabetisch, „niemand" unten), und
 * jede Zahl öffnet die Aufgaben dahinter.
 *
 * Was nicht mehr hier steht: die Stufen 10 T / 30 T / 3 Mon (Klick auf „wartet" zeigt die
 * Aufgaben, das Aufgabendetail den Termin), die Größen (Gruppen im Popup von „wartet") und
 * „möglich" (steht jetzt im Zuweisen-Dialog, wo die Frage gestellt wird).
 */
function TeamTabelle({
  team,
  zeigen,
  tagZeigen,
  berichtZeigen,
}: {
  team: LeitstandHeute['team']
  zeigen: (titel: string, ids: string[], gruppen?: AufgabenGruppe[]) => void
  tagZeigen: (a: TagesAuswahl) => void
  berichtZeigen: (personId: string) => void
}) {
  const [aufgeklappt, setAufgeklappt] = useState(false)
  const abwesenheiten = useAbwesenheiten()
  const zuletzt = wochentagKurz(team.letzterWerktag)
  const kopfBasis = 'px-1.5 py-1 text-[10.5px] font-semibold whitespace-nowrap text-ink2'
  const kopf = `${kopfBasis} text-right`

  return (
    <Card>
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2">
          <b className="text-[13.5px]">Team</b>
          <span className="text-xs text-muted">
            wer hat wie viel vor sich · Name öffnet den Aktivitätsbericht
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="mt-2 w-full min-w-[560px] border-collapse text-[12.5px]">
            <thead>
              <tr className="border-b border-line2">
                <th className={`${kopfBasis} text-left`}>Person</th>
                <th className={kopf} title="In Arbeit und Review gegen das WIP-Limit">
                  in Arbeit
                </th>
                <th className={kopf} title="Zugewiesen, nicht begonnen – ohne Altlasten">
                  wartet
                </th>
                <th className={kopf}>überfällig</th>
                <th className={kopf} title="Heute oder in den nächsten 5 Werktagen fällig">
                  ≤ 5 T
                </th>
                <th
                  className={kopf}
                  title={`Am ${tagKompakt(team.letzterWerktag)} auf erledigt gesetzt`}
                >
                  erl. {zuletzt}
                </th>
                <th className={kopf} title="Ereignisse der letzten 10 Werktage">
                  10 Werktage
                </th>
              </tr>
            </thead>
            <tbody>
              {team.zeilen.map((z) => (
                <TeamReihe
                  key={z.personId ?? 'niemand'}
                  z={z}
                  spurTage={team.spurTage}
                  letzterWerktag={team.letzterWerktag}
                  abwesenheiten={abwesenheiten}
                  zeigen={zeigen}
                  tagZeigen={tagZeigen}
                  berichtZeigen={berichtZeigen}
                />
              ))}
            </tbody>
          </table>
        </div>

        {team.ohneArbeit.length > 0 && (
          <div className="mt-2 text-xs text-muted">
            + {team.ohneArbeit.length} {team.ohneArbeit.length === 1 ? 'Person' : 'Personen'} ohne
            offene Arbeit{' '}
            <button
              type="button"
              onClick={() => setAufgeklappt(!aufgeklappt)}
              aria-expanded={aufgeklappt}
              className="cursor-pointer font-semibold text-accent-deep hover:underline"
            >
              {aufgeklappt ? 'zuklappen' : 'aufklappen'}
            </button>
            {aufgeklappt && (
              <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-ink2">
                {team.ohneArbeit.map((p) => (
                  <button
                    key={p.personId}
                    type="button"
                    onClick={() => berichtZeigen(p.personId)}
                    className="cursor-pointer hover:underline"
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </CardPad>
    </Card>
  )
}

function TeamReihe({
  z,
  spurTage,
  letzterWerktag,
  abwesenheiten,
  zeigen,
  tagZeigen,
  berichtZeigen,
}: {
  z: TeamZeile
  spurTage: string[]
  letzterWerktag: string
  abwesenheiten: ReturnType<typeof useAbwesenheiten>
  zeigen: (titel: string, ids: string[], gruppen?: AufgabenGruppe[]) => void
  tagZeigen: (a: TagesAuswahl) => void
  berichtZeigen: (personId: string) => void
}) {
  const niemand = z.personId === null
  const zelle = 'px-1.5 py-[7px] text-right tabular whitespace-nowrap'
  const weg = z.personId ? abwesenheiten.abwesendAn(z.personId, spurTage) : new Set<string>()
  const hoechst = Math.max(4, ...z.spur)
  const voll = z.wipLimit !== null && z.inArbeit.length >= z.wipLimit

  return (
    <tr className={`border-b border-line ${niemand ? 'bg-[#fbfaf8]' : ''}`}>
      <td className="px-1.5 py-[7px] text-left">
        {niemand ? (
          <span className="text-ink2 italic">niemand</span>
        ) : (
          <span className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => berichtZeigen(z.personId!)}
              className="cursor-pointer text-left font-semibold whitespace-nowrap hover:underline"
            >
              {z.name}
            </button>
            <AbwesenheitsHinweis
              angabe={angabeAusLage(abwesenheiten.lage.get(z.personId!), abwesenheiten.heute)}
            />
          </span>
        )}
      </td>
      <td className={zelle}>
        {niemand ? (
          <Zahl ids={z.inArbeit} oeffnen={() => zeigen('niemand · in Arbeit', z.inArbeit)} />
        ) : (
          <span className="inline-flex items-center gap-1.5">
            {z.wipLimit !== null && (
              <span
                aria-hidden
                className={`h-[5px] w-[34px] overflow-hidden rounded bg-accent-soft ${
                  weg.has(abwesenheiten.heute) ? 'opacity-45' : ''
                }`}
              >
                <i
                  className={`block h-full ${voll ? 'bg-pool-sofort' : 'bg-accent'}`}
                  style={{ width: `${Math.min(100, (z.inArbeit.length / z.wipLimit) * 100)}%` }}
                />
              </span>
            )}
            <Zahl
              ids={z.inArbeit}
              text={`${z.inArbeit.length}${z.wipLimit !== null ? ` / ${z.wipLimit}` : ''}`}
              oeffnen={() => zeigen(`${z.name} · in Arbeit`, z.inArbeit)}
            />
            {z.genommen.length > 0 && (
              <span
                className="text-[11px] text-muted"
                title="In Work genommen, noch nicht begonnen"
              >
                +{z.genommen.length}
              </span>
            )}
          </span>
        )}
      </td>
      <td className={zelle}>
        <Zahl
          ids={z.wartet}
          oeffnen={() =>
            zeigen(`${z.name} · wartet`, z.wartet, [
              { titel: 'groß', ids: z.groessen.gross },
              { titel: 'mittel', ids: z.groessen.mittel },
              { titel: 'klein', ids: z.groessen.klein },
            ])
          }
        />
      </td>
      <td className={zelle}>
        <Zahl
          ids={z.ueberfaellig}
          rot
          oeffnen={() => zeigen(`${z.name} · überfällig`, z.ueberfaellig)}
        />
      </td>
      <td className={zelle}>
        <Zahl ids={z.faellig5} oeffnen={() => zeigen(`${z.name} · fällig ≤ 5 T`, z.faellig5)} />
      </td>
      <td className={zelle}>
        {niemand ? (
          <span className="text-[11px] text-muted">
            {z.ohneDatum.length > 0 && (
              <button
                type="button"
                onClick={() => zeigen('niemand · ohne Datum', z.ohneDatum)}
                className="cursor-pointer hover:underline"
              >
                + {z.ohneDatum.length} ohne Datum
              </button>
            )}
          </span>
        ) : z.zuletztErledigt === 0 ? (
          <Leer />
        ) : (
          <button
            type="button"
            onClick={() => tagZeigen({ personId: z.personId, name: z.name, tag: letzterWerktag })}
            className="cursor-pointer hover:underline"
          >
            {z.zuletztErledigt}
          </button>
        )}
      </td>
      <td className="px-1.5 py-[7px] text-right">
        {!niemand && (
          <span
            className="inline-flex h-4 items-end gap-[2px] align-[-3px]"
            role="img"
            aria-label={`Aktivität der letzten 10 Werktage: ${z.spur.join(', ')}`}
          >
            {z.spur.map((n, i) => {
              const t = spurTage[i] as string
              if (n === 0 && weg.has(t)) {
                return (
                  <i
                    key={t}
                    title={`${tagKompakt(t)}: abwesend`}
                    className="block h-4 w-1 rounded-[1px]"
                    style={{
                      background:
                        'repeating-linear-gradient(135deg, var(--color-line2) 0 2px, transparent 2px 4px)',
                    }}
                  />
                )
              }
              return (
                <i
                  key={t}
                  title={`${tagKompakt(t)}: ${n} Ereignisse`}
                  className={`block w-1 rounded-[1px] ${i === z.spur.length - 1 ? 'bg-ink' : 'bg-[#c4c2bb]'}`}
                  style={{
                    height: `${n === 0 ? 1 : Math.max(3, Math.round((n / hoechst) * 16))}px`,
                  }}
                />
              )
            })}
          </span>
        )}
      </td>
    </tr>
  )
}

function Leer() {
  return <span className="text-line2">–</span>
}

/** Eine Zahl, die ihre Aufgaben zeigt. **Eine Null ist kein Knopf** – wie bisher im Leitstand. */
function Zahl({
  ids,
  oeffnen,
  text,
  rot = false,
}: {
  ids: string[]
  oeffnen: () => void
  text?: string
  rot?: boolean
}) {
  if (ids.length === 0) return text ? <span className="text-muted">{text}</span> : <Leer />
  return (
    <button
      type="button"
      onClick={oeffnen}
      className={`cursor-pointer hover:underline ${rot ? 'font-bold text-[#b02a2a]' : ''}`}
    >
      {text ?? ids.length}
    </button>
  )
}

/* ── Die Woche ────────────────────────────────────────────────────────────── */

/**
 * **Die Woche**, übernommen wie gebaut (Nils, 20.08. und 24.08.2026) – nur in derselben
 * Reihenfolge wie die Team-Tabelle darüber. Die Erledigt-Zahlen kommen fest aus 30 Tagen; der
 * Streifen blättert deshalb höchstens vier Wochen zurück (vorher hing das am Zeitraum-Umschalter,
 * den es in „Heute" nicht mehr gibt).
 */
function DieWoche({
  reihenfolge,
  zeigen,
  tagZeigen,
}: {
  reihenfolge: string[]
  zeigen: (p: Popup) => void
  tagZeigen: (a: TagesAuswahl) => void
}) {
  const [versatz, setVersatz] = useState(0)
  const { data: erledigt } = useQuery(erledigtProTagQuery(30))
  const { data: wochenAufgaben = [] } = useQuery(wochenAufgabenQuery)
  const abwesenheiten = useAbwesenheiten()
  const aufgabeOeffnen = useAufgabeOeffnen()

  const tage = (() => {
    const bezug = new Date()
    bezug.setDate(bezug.getDate() + versatz * 7)
    const montag = montagVon(bezug)
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(`${montag}T12:00:00`)
      d.setDate(d.getDate() + i)
      return tagesschluessel(d)
    })
  })()
  const heute = tagesschluessel(new Date())
  const erster = tage[0] as string
  const letzter = tage[6] as string
  const beschriftung = `KW ${isoKw(erster).slice(-2)} · ${tagKompakt(erster)}–${tagKompakt(letzter)}`

  const personen = (() => {
    const nach = new Map<
      string,
      {
        personId: string
        name: string
        erledigt: Record<string, number>
        aufgaben: typeof wochenAufgaben
      }
    >()
    for (const p of erledigt?.personen ?? []) {
      const inWoche = p.jeTag.filter((t) => tage.includes(t.tag))
      if (inWoche.reduce((n, t) => n + t.anzahl, 0) === 0) continue
      nach.set(p.personId, {
        personId: p.personId,
        name: p.name,
        erledigt: Object.fromEntries(inWoche.map((t) => [t.tag, t.anzahl])),
        aufgaben: [],
      })
    }
    for (const b of wochenAufgaben) {
      const schluessel = b.personId ?? 'niemand'
      const vorhanden = nach.get(schluessel)
      if (vorhanden) vorhanden.aufgaben.push(b)
      else
        nach.set(schluessel, {
          personId: schluessel,
          name: b.personName ?? 'niemand',
          erledigt: {},
          aufgaben: [b],
        })
    }
    const rang = new Map(reihenfolge.map((n, i) => [n, i]))
    return [...nach.values()].sort((a, b) => {
      if (a.personId === 'niemand') return 1
      if (b.personId === 'niemand') return -1
      const ra = rang.get(a.name) ?? Number.MAX_SAFE_INTEGER
      const rb = rang.get(b.name) ?? Number.MAX_SAFE_INTEGER
      return ra - rb || a.name.localeCompare(b.name, 'de')
    })
  })()

  return (
    <Card>
      <CardPad>
        <div className="mb-1 flex flex-wrap items-baseline gap-2">
          <b className="text-[13.5px]">Die Woche</b>
          <span className="text-xs text-muted">
            links vom Strich war, rechts ist Plan · Klick auf den Tag zeigt, was bei wem dran ist
          </span>
        </div>
        <WochenStreifen
          tage={tage}
          heute={heute}
          personen={personen}
          abwesendAn={abwesenheiten.abwesendAn}
          onBalkenKlick={aufgabeOeffnen}
          onTagKlick={(personId, name, tag) => tagZeigen({ personId, name, tag })}
          onTagSpalteKlick={(tag) => {
            const gruppen = tagNachPersonen(personen, tag, heute)
            const zahl = (lage: Tageslage) =>
              gruppen
                .filter((g) => g.lage === lage)
                .reduce((n, g) => n + g.aufgaben.filter((a) => !a.mit).length, 0)
            const teile = TAGESLAGEN.filter((l) => zahl(l) > 0).map(
              (l) => `${zahl(l)} ${TAGESLAGE_LABELS[l]}`,
            )
            zeigen({
              titel: `${tagLang(tag)} · was ist dran`,
              untertitel: teile.length > 0 ? teile.join(' · ') : 'an diesem Tag ist nichts dran',
              gruppen: gruppen.flatMap((g) =>
                [
                  { zusatz: '', aufgaben: g.aufgaben.filter((a) => !a.mit) },
                  { zusatz: ' · mit', aufgaben: g.aufgaben.filter((a) => a.mit) },
                ]
                  .filter((t) => t.aufgaben.length > 0)
                  .map((t) => ({
                    titel: `${g.name} · ${TAGESLAGE_LABELS[g.lage]}${t.zusatz}`,
                    ids: t.aufgaben.map((a) => a.taskId),
                  })),
              ),
            })
          }}
          onWartendKlick={(name, was, ids) =>
            zeigen({
              titel: `${name} · wartet · ${was}`,
              untertitel: anzahl(ids.length),
              gruppen: [{ ids }],
            })
          }
          beschriftung={beschriftung}
          zurueck={versatz > -4 ? () => setVersatz((v) => v - 1) : null}
          vor={versatz < 14 ? () => setVersatz((v) => v + 1) : null}
        />
      </CardPad>
    </Card>
  )
}

/** „Mittwoch, 27.08." – ein Daily wird in Wochentagen gesprochen. */
function tagLang(tag: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
  }).format(tagesbeginn(tag))
}
